<template>
  <div class="app-container">
    <el-alert
      v-if="platformStatus"
      class="redbook-platform-alert"
      :title="platformAlertTitle"
      :type="platformStatus.shareEnabled ? (platformStatus.oauthAuthorized ? 'success' : 'warning') : 'info'"
      :closable="false"
      show-icon
    >
      <div class="redbook-platform-alert__body">
        <span>回调：{{ platformStatus.callbackUrl }}</span>
        <span v-if="platformStatus.pushUrl"> · 推送：{{ platformStatus.pushUrl }}</span>
        <span v-if="platformStatus.oauthAuthorized && platformStatus.authorizedSeller">
          · 已授权：{{ authorizedSellerLabel }}
        </span>
        <span v-if="platformStatus.shareAccessTokenOk === false" class="redbook-platform-alert__ref">
          · 分享鉴权失败：{{ platformStatus.shareAccessTokenMessage || 'app 未在分享开放平台注册' }}
        </span>
        <a
          v-if="platformStatus.shareRegisterUrl"
          class="redbook-platform-alert__link"
          :href="platformStatus.shareRegisterUrl"
          target="_blank"
          rel="noopener"
        >分享 SDK 文档</a>
        <span v-if="platformStatus.testShopReference" class="redbook-platform-alert__ref">
          · 联调参考：{{ platformStatus.testShopReference.name }}（{{ platformStatus.testShopReference.email }}，非授权店铺）
        </span>
        <el-button
          v-if="platformStatus.authorizeUrl && !platformStatus.oauthAuthorized"
          type="text"
          size="mini"
          @click="openAuthorize"
        >
          去授权
        </el-button>
        <el-button type="text" size="mini" @click="loadPlatformStatus">刷新</el-button>
      </div>
    </el-alert>

    <div class="head-container">
      <div class="crud-opts">
        <span class="crud-opts-left">
          <el-button
            class="filter-item"
            type="primary"
            size="mini"
            icon="el-icon-plus"
            @click="openForm()"
          >
            新增
          </el-button>
        </span>
      </div>

      <div class="filter-container">
        <el-input
          v-model="query.keyword"
          clearable
          size="small"
          placeholder="活动名称/编码"
          class="filter-item"
          style="width: 200px; margin-right: 10px;"
          @keyup.enter.native="loadList"
        />
        <el-select
          v-model="query.status"
          clearable
          size="small"
          placeholder="状态"
          class="filter-item"
          style="width: 130px; margin-right: 10px;"
        >
          <el-option label="启用" :value="1" />
          <el-option label="停用" :value="0" />
        </el-select>
        <el-button
          class="filter-item"
          size="mini"
          type="primary"
          icon="el-icon-search"
          @click="loadList"
        >
          查询
        </el-button>
        <el-button
          class="filter-item"
          size="mini"
          icon="el-icon-refresh-left"
          @click="resetQuery"
        >
          重置
        </el-button>
      </div>
    </div>

    <el-table v-loading="loading" :data="list" size="small" style="width: 100%;">
      <el-table-column prop="activityName" label="活动名称" min-width="140" show-overflow-tooltip />
      <el-table-column prop="activityCode" label="活动编码" min-width="160" show-overflow-tooltip />
      <el-table-column prop="status" label="状态" width="80" align="center">
        <template slot-scope="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini">
            {{ row.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="locationName" label="位置" min-width="120" show-overflow-tooltip />
      <el-table-column prop="createTime" label="创建时间" width="160" />
      <el-table-column label="操作" min-width="260" width="260" align="center" fixed="right" class-name="admin-pc-table-actions-col">
        <template slot-scope="{ row }">
          <div class="admin-pc-table-actions">
            <el-button type="text" size="mini" @click="openForm(row.id)">编辑</el-button>
            <el-button type="text" size="mini" @click="openNfc(row)">NFC</el-button>
            <el-button type="text" size="mini" @click="previewLaunch(row)">预览</el-button>
            <el-button type="text" size="mini" @click="openRecords(row)">记录</el-button>
            <el-button type="text" size="mini" class="admin-pc-table-actions__danger" @click="handleDelete(row)">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      :total="total"
      :current-page="query.page + 1"
      :page-size="query.size"
      :page-sizes="[10, 20, 50]"
      style="margin-top: 8px;"
      layout="total, prev, pager, next, sizes"
      @size-change="onSizeChange"
      @current-change="onPageChange"
    />

    <el-dialog
      :title="form.id ? '编辑活动' : '新建活动'"
      :visible.sync="formVisible"
      width="780px"
      :close-on-click-modal="false"
      @closed="resetForm"
    >
      <el-form ref="formRef" :model="form" :rules="formRules" label-width="96px" size="small" class="admin-pc-dialog-form admin-pc-dialog-form--grid">
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="活动名称" prop="activityName">
              <el-input v-model="form.activityName" maxlength="128" placeholder="请输入活动名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="状态">
              <el-radio-group v-model="form.status">
                <el-radio :label="1">启用</el-radio>
                <el-radio :label="0">停用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="24">
            <el-form-item label="有效期">
              <el-date-picker
                v-model="form.timeRange"
                type="datetimerange"
                value-format="yyyy-MM-dd HH:mm:ss"
                start-placeholder="开始"
                end-placeholder="结束"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="绑定门店">
              <el-select v-model="form.storeId" filterable clearable placeholder="选择门店" style="width: 100%">
                <el-option v-for="s in storeOptions" :key="s.storeId" :label="s.storeName + ' (' + s.storeCode + ')'" :value="s.storeId" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="限制字数">
              <div class="admin-pc-form-inline">
                <el-input-number
                  v-model="form.wordLimit"
                  :min="0"
                  :max="5000"
                  :step="10"
                  controls-position="right"
                />
                <span class="admin-pc-form-hint">0 表示不限制</span>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="位置名称">
              <el-input v-model="form.locationName" placeholder="POI 名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="详细地址">
              <el-input v-model="form.locationAddress" placeholder="详细地址" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="纬度">
              <el-input v-model="form.latitude" placeholder="纬度" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="经度">
              <el-input v-model="form.longitude" placeholder="经度" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="活动图片">
              <admin-multi-image-upload v-model="form.imageUrls" :limit="5" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="话题">
              <div class="admin-pc-form-block">
                <div v-for="(t, idx) in form.topics" :key="'topic-' + idx" class="admin-pc-form-block__row">
                  <el-input v-model="form.topics[idx]" placeholder="不含#，如：周末去哪玩" />
                  <el-button type="text" size="mini" @click="form.topics.splice(idx, 1)">删除</el-button>
                </div>
                <el-button type="text" size="mini" @click="form.topics.push('')">+ 添加话题</el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="24">
            <el-form-item label="AI 提示词" prop="promptText">
              <el-input
                v-model="form.promptText"
                type="textarea"
                :rows="4"
                placeholder="用户碰 NFC 时按此提示词调用千问生成文案"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="24">
            <el-form-item label="备注">
              <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="备注说明" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button type="text" @click="formVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="submitForm">确认</el-button>
      </div>
    </el-dialog>

    <el-dialog :visible.sync="nfcVisible" width="820px" :close-on-click-modal="false">
      <div slot="title" class="admin-pc-nfc-dialog-title">
        <span class="admin-pc-nfc-dialog-title__main">NFC 设备管理</span>
        <span v-if="currentActivity" class="admin-pc-nfc-dialog-title__activity">
          活动：{{ currentActivity.activityName }}（{{ currentActivity.activityCode }}）
        </span>
      </div>
      <div class="head-container">
        <el-input
          v-model="nfcForm.deviceName"
          placeholder="设备名称，如：1号门入口"
          class="filter-item"
          style="width: 200px; margin-right: 10px;"
        />
        <el-button class="filter-item" type="primary" size="mini" :loading="nfcCreating" @click="createNfcDevice">新增</el-button>
      </div>
      <el-table :data="nfcList" size="small" style="width: 100%;">
        <el-table-column prop="deviceName" label="设备名" width="110" />
        <el-table-column prop="nfcCode" label="编码" width="130" show-overflow-tooltip />
        <el-table-column prop="status" label="状态" width="80" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini">
              {{ row.status === 1 ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="nfcUrl" label="NFC URL" min-width="200" show-overflow-tooltip />
        <el-table-column label="操作" min-width="260" width="260" align="center" class-name="admin-pc-table-actions-col">
          <template slot-scope="{ row }">
            <div class="admin-pc-table-actions">
              <el-button type="text" size="mini" @click="copyNfcUrl(row)">复制</el-button>
              <el-button type="text" size="mini" @click="editNfcDevice(row)">编辑</el-button>
              <el-button type="text" size="mini" @click="toggleNfcStatus(row)">
                {{ row.status === 1 ? '停用' : '启用' }}
              </el-button>
              <el-button type="text" size="mini" @click="regenerateNfc(row)">重新生成</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog title="用户发布记录" :visible.sync="recordVisible" width="1000px" :close-on-click-modal="false">
      <el-table v-loading="recordLoading" :data="recordList" size="small" style="width: 100%;">
        <el-table-column prop="recordNo" label="记录号" min-width="160" show-overflow-tooltip />
        <el-table-column prop="nfcCode" label="NFC编码" width="120" show-overflow-tooltip />
        <el-table-column prop="storeName" label="门店" width="100" show-overflow-tooltip />
        <el-table-column prop="contentText" label="生成文案" min-width="180" show-overflow-tooltip />
        <el-table-column prop="contentSource" label="来源" width="70" align="center" />
        <el-table-column prop="clientOs" label="系统" width="70" align="center" />
        <el-table-column prop="publishStatus" label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            {{ publishStatusText(row.publishStatus) }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="时间" width="160" />
      </el-table>
      <el-pagination
        :total="recordTotal"
        :current-page="recordQuery.page + 1"
        :page-size="recordQuery.size"
        :page-sizes="[10, 20, 50]"
        style="margin-top: 8px;"
        layout="total, prev, pager, next, sizes"
        @size-change="onRecordSizeChange"
        @current-change="onRecordPageChange"
      />
    </el-dialog>
  </div>
</template>

<script>
import {
  createRedbookNfc,
  deleteRedbookActivity,
  fetchRedbookActivities,
  fetchRedbookActivityDetail,
  fetchRedbookPlatformStatus,
  fetchRedbookPublishRecords,
  fetchRedbookStores,
  regenerateRedbookNfcUrl,
  saveRedbookActivity,
  setRedbookNfcStatus,
  updateRedbookNfc
} from '@/api/h5-admin-redbook'
import AdminMultiImageUpload from '../components/AdminMultiImageUpload.vue'

export default {
  name: 'AdminRedbookIndex',
  components: { AdminMultiImageUpload },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: { keyword: '', status: null, page: 0, size: 10 },
      formVisible: false,
      saving: false,
      form: this.emptyForm(),
      formRules: {
        activityName: [{ required: true, message: '请输入活动名称', trigger: 'blur' }],
        promptText: [{ required: true, message: '请填写 AI 提示词', trigger: 'blur' }]
      },
      storeOptions: [],
      nfcVisible: false,
      nfcCreating: false,
      nfcForm: { deviceName: '' },
      nfcList: [],
      currentActivity: null,
      recordVisible: false,
      recordLoading: false,
      recordList: [],
      recordTotal: 0,
      recordQuery: { activityId: null, page: 0, size: 20 },
      platformStatus: null
    }
  },
  computed: {
    platformAlertTitle() {
      if (!this.platformStatus) return '小红书开放平台'
      const name = this.platformStatus.appName || '碰一碰打卡'
      if (!this.platformStatus.shareEnabled) {
        return name + '：SDK 未启用'
      }
      if (this.platformStatus.shareAccessTokenOk === false) {
        return name + '：SDK 已配置，但分享应用未注册或鉴权失败'
      }
      if (this.platformStatus.oauthAuthorized) {
        const seller = this.platformStatus.authorizedSeller || {}
        const label = seller.sellerName || seller.sellerId || '已授权'
        return name + '：分享 SDK 可用，商家已授权（' + label + '）'
      }
      return name + '：分享 SDK 可用，建议完成商家 OAuth 授权'
    },
    authorizedSellerLabel() {
      const seller = (this.platformStatus && this.platformStatus.authorizedSeller) || {}
      return seller.sellerName || seller.sellerId || '已授权'
    }
  },
  created() {
    this.loadList()
    this.loadStores()
    this.loadPlatformStatus()
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        activityName: '',
        status: 1,
        timeRange: [],
        storeId: null,
        imageUrls: [],
        topics: [''],
        locationName: '',
        locationAddress: '',
        latitude: '',
        longitude: '',
        promptText: '',
        wordLimit: null,
        remark: ''
      }
    },
    loadList() {
      this.loading = true
      const params = {
        page: this.query.page,
        size: this.query.size,
        status: this.query.status,
        keyword: this.query.keyword || undefined
      }
      fetchRedbookActivities(params)
        .then(res => {
          const data = res.data || {}
          this.list = data.content || []
          this.total = data.totalElements || 0
        })
        .finally(() => { this.loading = false })
    },
    resetQuery() {
      this.query.keyword = ''
      this.query.status = null
      this.query.page = 0
      this.loadList()
    },
    onSizeChange(size) {
      this.query.size = size
      this.query.page = 0
      this.loadList()
    },
    loadStores() {
      fetchRedbookStores().then(res => {
        this.storeOptions = res.data || []
      })
    },
    loadPlatformStatus() {
      fetchRedbookPlatformStatus().then(res => {
        this.platformStatus = res.data || null
      }).catch(() => {})
    },
    openAuthorize() {
      if (!this.platformStatus || !this.platformStatus.authorizeUrl) {
        this.$message.warning('暂无授权链接')
        return
      }
      window.open(this.platformStatus.authorizeUrl, '_blank')
    },
    onPageChange(page) {
      this.query.page = page - 1
      this.loadList()
    },
    openForm(id) {
      if (!id) {
        this.form = this.emptyForm()
        this.formVisible = true
        return
      }
      fetchRedbookActivityDetail(id).then(res => {
        const d = res.data || {}
        this.form = {
          id: d.id,
          activityName: d.activityName,
          status: d.status,
          timeRange: d.startTime && d.endTime ? [d.startTime, d.endTime] : [],
          storeId: d.storeId || null,
          imageUrls: (d.imageUrls && d.imageUrls.length) ? d.imageUrls : [],
          topics: (d.topics && d.topics.length) ? d.topics : [''],
          locationName: d.locationName || '',
          locationAddress: d.locationAddress || '',
          latitude: d.latitude != null ? String(d.latitude) : '',
          longitude: d.longitude != null ? String(d.longitude) : '',
          promptText: d.promptText || '',
          wordLimit: d.wordLimit && d.wordLimit > 0 ? d.wordLimit : null,
          remark: d.remark || ''
        }
        this.formVisible = true
      })
    },
    resetForm() {
      this.form = this.emptyForm()
      if (this.$refs.formRef) this.$refs.formRef.clearValidate()
    },
    submitForm() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        this.saving = true
        const payload = {
          id: this.form.id,
          activityName: this.form.activityName,
          status: this.form.status,
          startTime: this.form.timeRange && this.form.timeRange[0] ? this.form.timeRange[0] : null,
          endTime: this.form.timeRange && this.form.timeRange[1] ? this.form.timeRange[1] : null,
          storeId: this.form.storeId || null,
          imageUrls: this.form.imageUrls.filter(Boolean),
          topics: this.form.topics.filter(Boolean),
          locationName: this.form.locationName,
          locationAddress: this.form.locationAddress,
          latitude: this.form.latitude ? Number(this.form.latitude) : null,
          longitude: this.form.longitude ? Number(this.form.longitude) : null,
          promptText: this.form.promptText,
          wordLimit: this.form.wordLimit,
          remark: this.form.remark
        }
        saveRedbookActivity(payload)
          .then(() => {
            this.$message.success('保存成功')
            this.formVisible = false
            this.loadList()
          })
          .finally(() => { this.saving = false })
      })
    },
    handleDelete(row) {
      this.$confirm('确定删除该活动吗？', '提示', { type: 'warning' }).then(() => {
        deleteRedbookActivity(row.id).then(() => {
          this.$message.success('已删除')
          this.loadList()
        })
      }).catch(() => {})
    },
    openNfc(row) {
      this.currentActivity = row
      this.nfcForm.deviceName = ''
      fetchRedbookActivityDetail(row.id).then(res => {
        this.nfcList = (res.data && res.data.nfcDevices) || []
        this.nfcVisible = true
      })
    },
    createNfcDevice() {
      if (!this.currentActivity) return
      this.nfcCreating = true
      createRedbookNfc(this.currentActivity.id, { deviceName: this.nfcForm.deviceName })
        .then(() => {
          this.$message.success('NFC 设备已创建')
          return fetchRedbookActivityDetail(this.currentActivity.id)
        })
        .then(res => {
          this.nfcList = (res.data && res.data.nfcDevices) || []
        })
        .finally(() => { this.nfcCreating = false })
    },
    regenerateNfc(row) {
      if (!this.currentActivity) return
      regenerateRedbookNfcUrl(this.currentActivity.id, row.nfcCode).then(res => {
        row.nfcUrl = (res.data && res.data.nfcUrl) || row.nfcUrl
        this.$message.success('URL 已更新')
      })
    },
    editNfcDevice(row) {
      if (!this.currentActivity) return
      this.$prompt('请输入设备名称', '编辑 NFC 设备', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValue: row.deviceName || '',
        inputPattern: /\S+/,
        inputErrorMessage: '设备名称不能为空'
      }).then(({ value }) => {
        return updateRedbookNfc(this.currentActivity.id, row.nfcCode, { deviceName: value.trim() })
      }).then(res => {
        const updated = res.data || {}
        if (updated.deviceName) {
          row.deviceName = updated.deviceName
        }
        this.$message.success('设备已更新')
      }).catch(() => {})
    },
    copyNfcUrl(row) {
      const text = row && row.nfcUrl
      if (!text) {
        this.$message.warning('暂无 NFC URL')
        return
      }
      this.copyToClipboard(text, 'NFC URL 已复制')
    },
    copyToClipboard(text, successMsg) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(successMsg || '已复制'))
          .catch(() => this.fallbackCopy(text, successMsg))
        return
      }
      this.fallbackCopy(text, successMsg)
    },
    fallbackCopy(text, successMsg) {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      try {
        const ok = document.execCommand('copy')
        if (ok) {
          this.$message.success(successMsg || '已复制')
        } else {
          this.$message.error('复制失败，请手动复制')
        }
      } catch (e) {
        this.$message.error('复制失败，请手动复制')
      } finally {
        document.body.removeChild(ta)
      }
    },
    openRecords(row) {
      this.recordVisible = true
      this.recordQuery.activityId = row.id
      this.recordQuery.page = 0
      this.loadRecords()
    },
    loadRecords() {
      this.recordLoading = true
      fetchRedbookPublishRecords({
        activityId: this.recordQuery.activityId,
        page: this.recordQuery.page,
        size: this.recordQuery.size
      })
        .then(res => {
          const data = res.data || {}
          this.recordList = data.content || []
          this.recordTotal = data.totalElements || 0
        })
        .finally(() => { this.recordLoading = false })
    },
    onRecordSizeChange(size) {
      this.recordQuery.size = size
      this.recordQuery.page = 0
      this.loadRecords()
    },
    onRecordPageChange(page) {
      this.recordQuery.page = page - 1
      this.loadRecords()
    },
    previewLaunch(row) {
      if (!row || !row.activityCode) {
        this.$message.warning('缺少活动编码')
        return
      }
      const base = window.location.origin + window.location.pathname
      const url = base + '#/redbook/launch?activity_code=' + encodeURIComponent(row.activityCode)
      window.open(url, '_blank')
    },
    toggleNfcStatus(row) {
      if (!this.currentActivity || !row) return
      const nextStatus = row.status === 1 ? 0 : 1
      const action = nextStatus === 1 ? '启用' : '停用'
      this.$confirm('确定' + action + '该 NFC 设备吗？', '提示', { type: 'warning' }).then(() => {
        return setRedbookNfcStatus(this.currentActivity.id, row.nfcCode, { status: nextStatus })
      }).then(res => {
        const updated = res.data || {}
        row.status = updated.status != null ? updated.status : nextStatus
        this.$message.success('已' + action)
      }).catch(() => {})
    },
    publishStatusText(status) {
      const map = { 0: '打开页', 1: '已唤起', 2: '用户确认', 3: '失败' }
      return map[status] != null ? map[status] : status
    }
  }
}
</script>

<style scoped>
.redbook-platform-alert {
  margin-bottom: 12px;
}

.redbook-platform-alert__body {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  line-height: 1.6;
}

.redbook-platform-alert__ref {
  color: #909399;
}

.redbook-platform-alert__link {
  margin-left: 8px;
  color: #409eff;
  text-decoration: none;
}
</style>
